import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Divider,
  Button,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbsUpDownIcon from '@mui/icons-material/ThumbsUpDown';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RuleIcon from "@mui/icons-material/Rule";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigation } from "../../../hooks/useNavigation";

interface PortfolioCardProps {
  portfolioId: number;
  title: string;
  projectCount: number;
  counts: { green: number; orange: number; red: number };
  projectId: string;
  onViewDetails?: () => void;
  onViewProjects?: () => void;
  ragStatus: {
    time: "green" | "red" | "amber";
    cost: "green" | "red" | "amber";
    quality: "green" | "red" | "amber";
    overall: "green" | "red" | "amber";
  };
}

export const PortfolioCard: React.FC<PortfolioCardProps> = ({
  portfolioId,
  title,
  projectCount,
  counts,
  projectId,
  onViewDetails,
  onViewProjects,
  ragStatus
}) => {

  const { goToEditPortfolio } = useNavigation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  }
  return (
    <Card
      variant="outlined"
      sx={{
        borderTop: 3,
        borderTopColor:
          ragStatus.overall === "green"
            ? "success.main"
            : ragStatus.overall === "amber"
              ? "warning.main"
              : "error.main",
      }}
    >
      <CardContent sx={{ px: 2, pb: 0.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body2"
            sx={{
              maxWidth: "80%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip
              color="white"
              title={
                <Box sx={{ p: 0.5, py: 1, display: "flex", gap: 1 }}>
                  <CheckCircleIcon
                    sx={{ color: "success.main", fontSize: "1rem" }}
                  />
                  <Typography variant="body2">
                    <b>Overall Portfolio RAG:</b> Based on <b>Cost, Time,</b>{" "}
                    and <b>Quality</b> performance. red overrides amber, amber
                    overrides green.
                  </Typography>
                </Box>
              }
              placement="top-start"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "background.paper",
                    color: "text.primary",
                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.32)",
                    borderColor: "divider",
                    borderRadius: "8px",
                    maxWidth: "300px",
                    border: "1px solid",
                  },
                },
              }}
            >
              {ragStatus.overall === "green" ? (
                <ThumbUpIcon
                  sx={{
                    cursor: "pointer",
                    color: "success.main",
                    fontSize: 20,
                  }}
                />
              ) : ragStatus.overall === "amber" ? (
                <ThumbsUpDownIcon
                  sx={{
                    cursor: "pointer",
                    color: "warning.main",
                    fontSize: 20,
                  }}
                />
              ) : (
                <ThumbDownAltIcon
                  sx={{
                    cursor: "pointer",
                    color: "error.main",
                    fontSize: 20,
                  }}
                />
              )}
            </Tooltip>

            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem
                onClick={() => goToEditPortfolio(portfolioId)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <EditIcon
                    sx={{ color: "primary.main" }}
                    fontSize="small"
                  />
                  <Typography variant="subtitle2">Edit Portfolio</Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Chip label={`${projectCount} Projects`} size="small" />
          <Chip
            label={counts.green}
            size="small"
            sx={{
              border: "1px solid",
              bgcolor: "transparent",
              borderColor: "success.main",
              color: "success.main",
            }}
          />
          <Chip
            label={counts.orange}
            size="small"
            sx={{
              border: "1px solid",
              bgcolor: "transparent",
              borderColor: "warning.main",
              color: "warning.main",
            }}
          />
          <Chip
            label={counts.red}
            size="small"
            sx={{
              border: "1px solid",
              bgcolor: "transparent",
              borderColor: "error.main",
              color: "error.main",
            }}
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            {projectId}
          </Typography>
          <Stack direction="row" spacing={1}>
            <PaymentsIcon
              sx={{
                fontSize: 22,
                color:
                  ragStatus.cost === "green"
                    ? "success.main"
                    : ragStatus.cost === "red"
                      ? "error.main"
                      : ragStatus.cost === "amber"
                        ? "warning.main"
                        : "default",
              }}
            />
            <AccessTimeIcon
              sx={{
                fontSize: 22,
                color:
                  ragStatus.time === "green"
                    ? "success.main"
                    : ragStatus.time === "red"
                      ? "error.main"
                      : ragStatus.time === "amber"
                        ? "warning.main"
                        : "default",
              }}
            />
            <RuleIcon
              sx={{
                fontSize: 22,
                color:
                  ragStatus.quality === "green"
                    ? "success.main"
                    : ragStatus.quality === "red"
                      ? "error.main"
                      : ragStatus.quality === "amber"
                        ? "warning.main"
                        : "default",
              }}
            />
          </Stack>
        </Stack>
      </CardContent>
      <Divider sx={{ mt: 1 }} />
      <Box display="flex" justifyContent="space-between" py={1}>
        <Button onClick={onViewDetails} fullWidth>
          VIEW DETAILS
        </Button>
        <Divider orientation="vertical" flexItem />
        <Button onClick={onViewProjects} fullWidth>
          VIEW PROJECTS
        </Button>
      </Box>
    </Card>
  );
};

export default PortfolioCard;